import {inject, Injectable, signal} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {ENV_TOKEN, EnvInterface} from 'env-config';
import {AuthValidationService} from 'auth';
import {SessionStoreDataService} from '../../projects/products-lib/src/lib/services/session-store-data.service';

const STORAGE_KEY_PREFIX = 'pupilIconUrl-';
const DISPLAY_NAME_KEY_PREFIX = 'pupilDisplayName-';

@Injectable({providedIn: 'root'})
export class PupilMemberService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(ENV_TOKEN) as EnvInterface;
  private readonly authValidationService = inject(AuthValidationService);
  private readonly sessionStoreDataService = inject(SessionStoreDataService);

  /** Reactive signal holding the pupil icon URL */
  readonly pupilIconUrl = signal('');

  /** Reactive signal holding the pupil display name */
  readonly pupilDisplayName = signal<string>('');

  /** Fetch the pupil icon URL from the API and store it in sessionStorage */
  loadPupilIcon(): void {
    const token = this.authValidationService.readAuthToken();
    if (!token) {
      return;
    }

    // Use pid query param if present, otherwise fall back to decoded token userGuid
    const pid = new URLSearchParams(window.location.search).get('pid');
    let userGuid = pid || '';

    if (!userGuid) {
      const userData = this.sessionStoreDataService.getDecodedTokenUser();
      if (!userData?.userGuid) {
        return;
      }
      userGuid = userData.userGuid;
    }

    // Check if we already have a cached value for this user
    const cached = sessionStorage.getItem(STORAGE_KEY_PREFIX + userGuid);
    if (cached) {
      this.pupilIconUrl.set(cached);
      return;
    }

    const url = `${this.environment.security.tuatara.endpoint}/user-data/get-pupil-icon`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.environment.security.tuatara.apiKey,
      'Authorization': `Bearer ${token}`,
    });

    this.http.get<{ value: string }>(url, {headers, params: {userGuid}}).subscribe({
      next: (response) => {
        let iconUrl = response.value;
        iconUrl = this.environment.app.mediaBaseUrl + iconUrl;
        sessionStorage.setItem(STORAGE_KEY_PREFIX + userGuid, iconUrl);
        this.pupilIconUrl.set(iconUrl);
      },
      error: (err) => {
        console.error('Failed to load pupil icon:', err);
      },
    });
  }

  /** Fetch the pupil display name from the API and store it in sessionStorage */
  loadPupilDisplayName(): void {
    const token = this.authValidationService.readAuthToken();
    if (!token) {
      return;
    }

    const pid = new URLSearchParams(window.location.search).get('pid');
    let userGuid = pid || '';

    if (!userGuid) {
      const userData = this.sessionStoreDataService.getDecodedTokenUser();
      if (!userData?.userGuid) {
        return;
      }
      userGuid = userData.userGuid;
    }

    // Check if we already have a cached value for this user
    const cached = sessionStorage.getItem(DISPLAY_NAME_KEY_PREFIX + userGuid);
    if (cached) {
      this.pupilDisplayName.set(cached);
      return;
    }

    const url = `${this.environment.security.tuatara.endpoint}/user-data/get-pupil-display-name`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.environment.security.tuatara.apiKey,
      'Authorization': `Bearer ${token}`,
    });

    this.http.get<{ value: string }>(url, {headers, params: {userGuid}}).subscribe({
      next: (response) => {
        const displayName = response.value;
        sessionStorage.setItem(DISPLAY_NAME_KEY_PREFIX + userGuid, displayName);
        this.pupilDisplayName.set(displayName);
      },
      error: (err) => {
        console.error('Failed to load pupil display name:', err);
      },
    });
  }

  /** Get the pupil display name (signal value) */
  getPupilDisplayName(): string {
    return this.pupilDisplayName();
  }

  /** Clear all stored pupil icon and display name entries (e.g. on logout) */
  clearPupilData(): void {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith(STORAGE_KEY_PREFIX) || key.startsWith(DISPLAY_NAME_KEY_PREFIX))) {
        sessionStorage.removeItem(key);
      }
    }
    this.pupilIconUrl.set('');
    this.pupilDisplayName.set('');
  }

  /** @deprecated Use clearPupilData() instead */
  clearPupilIcon(): void {
    this.clearPupilData();
  }
}





